import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Building2, Upload, Printer } from 'lucide-react';
import axios from 'axios';
import FileUpload from './FileUpload';
import CopyableLabel from './shared/CopyableLabel';
import RecordReportDialog from './RecordReportDialog';
import useSubTabShortcut from '../hooks/useSubTabShortcut';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyProfile = ({ user }) => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    tax_id: '',
    registration_number: '',
    currency: 'USD',
    date_format: 'MM/DD/YYYY',
    time_format: '12h',
    timezone: 'UTC',
    bank_pln_iban: '', bank_pln_swift: '', bank_pln_name: '',
    bank_eur_iban: '', bank_eur_swift: '', bank_eur_name: '',
    bank_gbp_iban: '', bank_gbp_swift: '', bank_gbp_name: '',
    bank_usd_iban: '', bank_usd_swift: '', bank_usd_name: '',
    // Legal representatives (surfaced as {{ordering_party.rep_N.*}} tokens
    // in contract templates). Two slots are supported.
    rep_1_first_name: '', rep_1_last_name: '', rep_1_position: '',
    rep_1_phone: '', rep_1_email: '', rep_1_pesel: '',
    rep_1_passport_number: '', rep_1_id_card_number: '',
    rep_2_first_name: '', rep_2_last_name: '', rep_2_position: '',
    rep_2_phone: '', rep_2_email: '', rep_2_pesel: '',
    rep_2_passport_number: '', rep_2_id_card_number: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  useSubTabShortcut({
    tab: 'company', ctxKey: 'companySubTab', value: activeTab, setValue: setActiveTab,
    labels: { details: 'auto.set.companyDetails', bank: 'auto.set.bankAccounts', reps: 'auto.set.representatives', logo: 'auto.set.logo' },
    allowed: ['admin', 'manager'].includes(user?.role) ? ['details', 'bank', 'reps', 'logo'] : ['details'],
  });
  const [reportOpen, setReportOpen] = useState(false);
  
  // Only admins and managers can edit company profile
  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/company/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching company profile:', error);
      toast.error(t('auto.set.failedToFetchCompanyProfile'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(`${API}/company/profile`, profile);
      toast.success(t('auto.set.companyProfileUpdatedSuccessfully'));
      fetchCompanyProfile(); // Refresh data
    } catch (error) {
      console.error('Error updating company profile:', error);
      toast.error(t('auto.set.failedToUpdateCompanyProfile'));
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoUploadComplete = (result) => {
    toast.success(t('auto.set.companyLogoUpdatedSuccessfully'));
    fetchCompanyProfile(); // Refresh to get updated logo
  };

  const handleHeaderLogoUploadComplete = (result) => {
    toast.success(t('auto.set.headerLogoUpdatedSuccessfully'));
    fetchCompanyProfile(); // Refresh to get updated header logo
  };

  const handleFaviconUploadComplete = (result) => {
    toast.success(t('auto.set.faviconUpdatedSuccessfully'));
    fetchCompanyProfile();
    // Nudge the running app to pick up the new favicon immediately —
    // useCompanyBranding polls, but this shaves off the wait.
    window.dispatchEvent(new Event('company-branding-changed'));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Building2 className="h-8 w-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{t('auto.set.companyProfile')}</h2>
            <p className="text-slate-600">{t('auto.set.manageYourCompanyInformationAndSettings')}</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setReportOpen(true)}
          data-testid="company-print-report-btn"
        >
          <Printer className="h-4 w-4 mr-1" />
          {t('auto.set.printReport')}
        </Button>
      </div>

      <RecordReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        kind="company"
        recordId={null}
        recordName={profile?.name || 'Company'}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="details" className="px-3 py-2" data-testid="tab-details">{t('auto.set.companyDetails')}</TabsTrigger>
          {canEdit && (
            <>
              <TabsTrigger value="bank" className="px-3 py-2" data-testid="tab-bank">{t('auto.set.bankAccounts')}</TabsTrigger>
              <TabsTrigger value="reps" className="px-3 py-2" data-testid="tab-reps">{t('auto.set.representatives')}</TabsTrigger>
              <TabsTrigger value="logo" className="px-3 py-2" data-testid="tab-logo">{t('auto.set.logo')}</TabsTrigger>
            </>
          )}
        </TabsList>
        
        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>{t('auto.set.companyInformation')}</CardTitle>
              {!canEdit && (
                <p className="text-sm text-amber-600">{t('auto.set.viewOnlyContactAnAdministratorTo')}</p>
              )}
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <CopyableLabel label={t('auto.set.companyName')} value={profile.name} testId="copy-company-name" />
                    <Input
                      value={profile.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder={t('auto.set.enterCompanyName')}
                      required
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div>
                    <CopyableLabel label={t('auto.set.email')} value={profile.email} testId="copy-company-email" />
                    <Input
                      type="email"
                      value={profile.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="company@example.com"
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div>
                    <CopyableLabel label={t('auto.set.phone')} value={profile.phone} testId="copy-company-phone" />
                    <Input
                      value={profile.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder={t('auto.set.enterPhoneNumber')}
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div>
                    <CopyableLabel label={t('auto.set.website')} value={profile.website} testId="copy-company-website" />
                    <Input
                      value={profile.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="https://www.company.com"
                      disabled={!canEdit}
                    />
                  </div>
                </div>

                <div>
                  <CopyableLabel label={t('auto.set.address')} value={profile.address} testId="copy-company-address" />
                  <Input
                    value={profile.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder={t('auto.set.streetAddress')}
                    disabled={!canEdit}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <CopyableLabel label={t('auto.set.city')} value={profile.city} testId="copy-company-city" />
                    <Input
                      value={profile.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder={t('auto.set.city')}
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div>
                    <CopyableLabel label={t('auto.set.stateProvince')} value={profile.state} testId="copy-company-state" />
                    <Input
                      value={profile.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      placeholder={t('auto.set.state')}
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div>
                    <CopyableLabel label={t('auto.set.postalCode')} value={profile.postal_code} testId="copy-company-postal" />
                    <Input
                      value={profile.postal_code}
                      onChange={(e) => handleInputChange('postal_code', e.target.value)}
                      placeholder="12345"
                      disabled={!canEdit}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <CopyableLabel label={t('auto.set.country')} value={profile.country} testId="copy-company-country" />
                    <Input
                      value={profile.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      placeholder={t('auto.set.country')}
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div>
                    <CopyableLabel label={t('auto.set.taxId')} value={profile.tax_id} testId="copy-company-tax-id" />
                    <Input
                      value={profile.tax_id}
                      onChange={(e) => handleInputChange('tax_id', e.target.value)}
                      placeholder={t('auto.set.taxIdentificationNumber')}
                      disabled={!canEdit}
                    />
                  </div>
                </div>

                <div>
                  <CopyableLabel label={t('auto.set.registrationNumber')} value={profile.registration_number} testId="copy-company-registration" />
                  <Input
                    value={profile.registration_number}
                    onChange={(e) => handleInputChange('registration_number', e.target.value)}
                    placeholder={t('auto.set.businessRegistrationNumber')}
                    disabled={!canEdit}
                  />
                </div>

                {canEdit && (
                  <Button type="submit" disabled={saving} className="w-full">
                    {saving ? t('auto.set.saving') : t('auto.set.saveCompanyDetails')}
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
        </TabsContent>
        
        {canEdit && (
          <TabsContent value="logo">
            <div className="space-y-6" data-testid="logo-tab-panel">
              {/* Company Logo (used in PDFs, reports, sidebar) */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('auto.set.companyLogo')}</CardTitle>
                  <p className="text-sm text-gray-600">
                    {t('auto.set.appearsOnPdfExportsInvoiceQuote')}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {profile.logo_file && (
                      <div className="text-center">
                        <img
                          src={`${BACKEND_URL}/api/company/logo/${profile.logo_file}`}
                          alt={t('auto.set.companyLogo')}
                          className="max-w-xs max-h-48 mx-auto rounded-lg shadow-sm"
                        />
                      </div>
                    )}
                    <FileUpload
                      type="logo"
                      entityId="company"
                      currentFile={profile.logo_file}
                      onUploadComplete={handleLogoUploadComplete}
                      onUploadError={() => toast.error(t('auto.set.failedToUploadLogo'))}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Header Logo (top-bar / login page) */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('auto.set.headerLogo')}</CardTitle>
                  <p className="text-sm text-gray-600">
                    Displayed in the application top-bar and on the login page.
                    Recommended: 32×32 to 128×128 px transparent PNG.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {profile.header_logo_file && (
                      <div className="text-center">
                        <img
                          src={`${BACKEND_URL}/api/company/header-logo/${profile.header_logo_file}`}
                          alt={t('auto.set.headerLogo')}
                          className="max-w-xs max-h-48 mx-auto rounded-lg shadow-sm"
                        />
                        <p className="text-sm text-gray-500 mt-2">Current: {profile.header_logo_file}</p>
                      </div>
                    )}
                    {!profile.header_logo_file && (
                      <div className="text-center p-8 border-2 border-dashed border-gray-200 rounded-lg">
                        <Building2 className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-600">{t('auto.set.noHeaderLogoUploaded')}</p>
                        <p className="text-xs text-gray-500">{t('auto.set.willUseTheCompanyLogoOr')}</p>
                      </div>
                    )}
                    <FileUpload
                      type="header-logo"
                      entityId="company"
                      currentFile={profile.header_logo_file}
                      onUploadComplete={handleHeaderLogoUploadComplete}
                      onUploadError={() => toast.error(t('auto.set.failedToUploadHeaderLogo'))}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Favicon (browser tab icon) */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('auto.set.websiteFavicon')}</CardTitle>
                  <p className="text-sm text-gray-600">
                    The small icon shown in the browser tab and bookmarks bar.
                    Upload a single <code>.png</code> (ideally 512×512 px) or{' '}
                    <code>.svg</code> — the system automatically generates{' '}
                    <code>.ico</code>, apple-touch-icon and PWA variants for you.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {profile.favicon_file && (
                      <div className="text-center">
                        <img
                          src={`${BACKEND_URL}/api/company/favicon/${profile.favicon_file}`}
                          alt={t('auto.set.favicon')}
                          className="w-16 h-16 mx-auto rounded-md shadow-sm bg-white border border-gray-200 p-1"
                          data-testid="favicon-preview"
                        />
                        <p className="text-sm text-gray-500 mt-2">Current: {profile.favicon_file}</p>
                      </div>
                    )}
                    {!profile.favicon_file && (
                      <div className="text-center p-6 border-2 border-dashed border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-600">{t('auto.set.noFaviconUploaded')}</p>
                        <p className="text-xs text-gray-500">{t('auto.set.browsersWillFallBackToThe')}</p>
                      </div>
                    )}
                    <FileUpload
                      type="favicon"
                      entityId="company"
                      currentFile={profile.favicon_file}
                      onUploadComplete={handleFaviconUploadComplete}
                      onUploadError={() => toast.error(t('auto.set.failedToUploadFavicon'))}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {canEdit && (
        <TabsContent value="bank">
          <Card>
            <CardHeader><CardTitle>{t('auto.set.bankAccountsByCurrency')}</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {[
                  { cur: 'PLN', label: t('auto.lbl.plnPolishZloty') },
                  { cur: 'EUR', label: t('auto.lbl.eurEuro') },
                  { cur: 'GBP', label: t('auto.lbl.gbpBritishPound') },
                  { cur: 'USD', label: t('auto.lbl.usdUsDollar') },
                ].map(({ cur, label }) => (
                  <div key={cur} className="border rounded-lg p-4 space-y-3">
                    <h3 className="font-semibold text-sm">{label}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <CopyableLabel label="IBAN" value={profile[`bank_${cur.toLowerCase()}_iban`]} testId={`copy-bank-${cur.toLowerCase()}-iban`} />
                        <Input value={profile[`bank_${cur.toLowerCase()}_iban`] || ''} onChange={e => setProfile(p => ({...p, [`bank_${cur.toLowerCase()}_iban`]: e.target.value}))} placeholder={`${cur} IBAN`} disabled={!canEdit} data-testid={`bank-${cur.toLowerCase()}-iban`} />
                      </div>
                      <div>
                        <CopyableLabel label="SWIFT/BIC" value={profile[`bank_${cur.toLowerCase()}_swift`]} testId={`copy-bank-${cur.toLowerCase()}-swift`} />
                        <Input value={profile[`bank_${cur.toLowerCase()}_swift`] || ''} onChange={e => setProfile(p => ({...p, [`bank_${cur.toLowerCase()}_swift`]: e.target.value}))} placeholder="SWIFT/BIC" disabled={!canEdit} data-testid={`bank-${cur.toLowerCase()}-swift`} />
                      </div>
                      <div>
                        <CopyableLabel label="Bank Name" value={profile[`bank_${cur.toLowerCase()}_name`]} testId={`copy-bank-${cur.toLowerCase()}-name`} />
                        <Input value={profile[`bank_${cur.toLowerCase()}_name`] || ''} onChange={e => setProfile(p => ({...p, [`bank_${cur.toLowerCase()}_name`]: e.target.value}))} placeholder="Bank name" disabled={!canEdit} data-testid={`bank-${cur.toLowerCase()}-name`} />
                      </div>
                    </div>
                  </div>
                ))}
                <Button type="submit" disabled={saving} data-testid="save-bank-accounts">{saving ? t('auto.set.saving') : t('auto.set.saveBankAccounts')}</Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {canEdit && (
        <TabsContent value="reps">
          <Card>
            <CardHeader>
              <CardTitle>{t('auto.set.companyLegalRepresentatives')}</CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                Up to two authorised representatives. These fields are
                exposed as tokens inside contract templates:{' '}
                <code>{'{{ordering_party.rep_1.first_name}}'}</code>,{' '}
                <code>{'{{ordering_party.rep_1.last_name}}'}</code>,{' '}
                <code>{'{{ordering_party.rep_1.position}}'}</code>,{' '}
                <code>{'{{ordering_party.rep_1.phone}}'}</code>,{' '}
                <code>{'{{ordering_party.rep_1.email}}'}</code>,{' '}
                <code>{'{{ordering_party.rep_1.pesel}}'}</code>,{' '}
                <code>{'{{ordering_party.rep_1.passport_number}}'}</code>,{' '}
                <code>{'{{ordering_party.rep_1.id_card_number}}'}</code>{' '}
                (and the same for <code>rep_2</code>). A convenience alias{' '}
                <code>{'{{ordering_party.rep_N.name}}'}</code> joins first
                and last name.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {[1, 2].map(n => (
                  <div key={n} className="border rounded-lg p-4 space-y-3">
                    <h3 className="font-semibold text-sm">Representative {n}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <CopyableLabel label="First Name" value={profile[`rep_${n}_first_name`]} testId={`copy-rep-${n}-first-name`} />
                        <Input value={profile[`rep_${n}_first_name`] || ''} onChange={e => handleInputChange(`rep_${n}_first_name`, e.target.value)} placeholder="e.g. Bartosz" data-testid={`rep-${n}-first-name`} />
                      </div>
                      <div>
                        <CopyableLabel label="Surname" value={profile[`rep_${n}_last_name`]} testId={`copy-rep-${n}-last-name`} />
                        <Input value={profile[`rep_${n}_last_name`] || ''} onChange={e => handleInputChange(`rep_${n}_last_name`, e.target.value)} placeholder="e.g. Pieczyński" data-testid={`rep-${n}-last-name`} />
                      </div>
                      <div>
                        <CopyableLabel label="Position / Title" value={profile[`rep_${n}_position`]} testId={`copy-rep-${n}-position`} />
                        <Input value={profile[`rep_${n}_position`] || ''} onChange={e => handleInputChange(`rep_${n}_position`, e.target.value)} placeholder="e.g. Managing Director" data-testid={`rep-${n}-position`} />
                      </div>
                      <div>
                        <CopyableLabel label="Phone" value={profile[`rep_${n}_phone`]} testId={`copy-rep-${n}-phone`} />
                        <Input value={profile[`rep_${n}_phone`] || ''} onChange={e => handleInputChange(`rep_${n}_phone`, e.target.value)} placeholder="+48 …" data-testid={`rep-${n}-phone`} />
                      </div>
                      <div>
                        <CopyableLabel label="Email" value={profile[`rep_${n}_email`]} testId={`copy-rep-${n}-email`} />
                        <Input type="email" value={profile[`rep_${n}_email`] || ''} onChange={e => handleInputChange(`rep_${n}_email`, e.target.value)} placeholder="name@company.eu" data-testid={`rep-${n}-email`} />
                      </div>
                      <div>
                        <CopyableLabel label="PESEL" value={profile[`rep_${n}_pesel`]} testId={`copy-rep-${n}-pesel`} />
                        <Input value={profile[`rep_${n}_pesel`] || ''} onChange={e => handleInputChange(`rep_${n}_pesel`, e.target.value)} placeholder="11-digit PESEL" data-testid={`rep-${n}-pesel`} />
                      </div>
                      <div>
                        <CopyableLabel label="Passport Number" value={profile[`rep_${n}_passport_number`]} testId={`copy-rep-${n}-passport-number`} />
                        <Input value={profile[`rep_${n}_passport_number`] || ''} onChange={e => handleInputChange(`rep_${n}_passport_number`, e.target.value)} placeholder="Passport #" data-testid={`rep-${n}-passport-number`} />
                      </div>
                      <div>
                        <CopyableLabel label="ID Card Number" value={profile[`rep_${n}_id_card_number`]} testId={`copy-rep-${n}-id-card-number`} />
                        <Input value={profile[`rep_${n}_id_card_number`] || ''} onChange={e => handleInputChange(`rep_${n}_id_card_number`, e.target.value)} placeholder="ID card #" data-testid={`rep-${n}-id-card-number`} />
                      </div>
                    </div>
                  </div>
                ))}
                <Button type="submit" disabled={saving} data-testid="save-representatives">{saving ? t('auto.set.saving') : t('auto.set.saveRepresentatives')}</Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default CompanyProfile;